// Cross-tenant view for AquaTrack staff. Search filters across pws_name,
// pws_id, and tenant_name. Results render as a flat ranked list (rather
// than tenant-grouped) when there's a query, since grouping fragments the
// match set.

import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { fetchPlatformActionItems, fetchSystems } from "@/api/endpoints";
import { ActionQueue } from "@/components/ActionQueue";
import { CardError, CardSkeleton } from "@/components/cards/CardSkeleton";
import { Highlight } from "@/components/Highlight";
import { InfoTip } from "@/components/InfoTip";
import { SearchBox } from "@/components/SearchBox";
import { SortSelect } from "@/components/SortSelect";
import { SystemBadges } from "@/components/SystemBadges";
import { TIPS } from "@/lib/glossary";
import { useInfiniteReveal } from "@/hooks/useInfiniteReveal";
import { useListKeyboardNav } from "@/hooks/useListKeyboardNav";
import { compareBySort, useListParams } from "@/hooks/useListParams";
import { useSystemSummary, type SummaryView } from "@/hooks/useSystemSummary";
import type { SystemSummary } from "@/types/api";
import { usePageTitle } from "@/hooks/usePageTitle";

interface TenantGroup {
  tenantId: string;
  tenantName: string;
  systems: SystemSummary[];
}

function matches(s: SystemSummary, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    s.pws_name.toLowerCase().includes(needle) ||
    s.pws_id.toLowerCase().includes(needle) ||
    (s.tenant_name?.toLowerCase().includes(needle) ?? false)
  );
}

function groupByTenant(systems: SystemSummary[]): TenantGroup[] {
  const map = new Map<string, TenantGroup>();
  for (const s of systems) {
    const id = s.tenant_id ?? "unknown";
    const name = s.tenant_name ?? "Unknown tenant";
    let group = map.get(id);
    if (!group) {
      group = { tenantId: id, tenantName: name, systems: [] };
      map.set(id, group);
    }
    group.systems.push(s);
  }
  return Array.from(map.values()).sort((a, b) =>
    a.tenantName.localeCompare(b.tenantName),
  );
}

export function PlatformDashboard(): JSX.Element {
  usePageTitle("Platform overview");
  const query = useQuery({
    queryKey: ["systems"],
    queryFn: fetchSystems,
  });
  const summary = useSystemSummary();
  // Cross-tenant action queue from the nightly snapshot. Supplementary — if it
  // fails or is empty it just doesn't render; the systems list stands alone.
  const actionQuery = useQuery({
    queryKey: ["platform-action-items"],
    queryFn: fetchPlatformActionItems,
  });
  const { q, sort, setQ, setSort } = useListParams("name");

  // Flat-results view is used whenever a search query is set OR a non-default
  // sort is active (sorting across tenants only makes sense flat).
  const flatMode = q.length > 0 || sort !== "name";

  const filteredSorted = useMemo<SystemSummary[]>(() => {
    const all = query.data ?? [];
    const filt = q ? all.filter((s) => matches(s, q)) : all;
    return [...filt].sort((a, b) => compareBySort(sort, a, b, summary.byPwsId));
  }, [query.data, q, sort, summary.byPwsId]);

  const grouped = useMemo<TenantGroup[]>(
    () => (flatMode ? [] : groupByTenant(filteredSorted)),
    [filteredSorted, flatMode],
  );

  // Flat list infinite reveal — used for the search-results / sort view.
  const { visible: flatVisible, hasMore: flatHasMore, sentinelRef: flatSentinel } =
    useInfiniteReveal(flatMode ? filteredSorted : []);

  // Grouped infinite reveal — pages tenant cards in chunks of 8.
  const { visible: groupVisible, hasMore: groupHasMore, sentinelRef: groupSentinel } =
    useInfiniteReveal(grouped, { pageSize: 8 });

  // Keyboard nav across the flat-mode results (only meaningful when flatMode).
  const flatHrefAt = useCallback(
    (i: number): string | null => {
      const s = flatVisible[i];
      return s ? `/systems/${encodeURIComponent(s.pws_id)}` : null;
    },
    [flatVisible],
  );
  const flatNav = useListKeyboardNav({
    count: flatMode ? flatVisible.length : 0,
    hrefAt: flatHrefAt,
  });

  // All hooks must be declared above the early-return guards (Rules of Hooks).
  const totalTenants = useMemo(
    () => new Set((query.data ?? []).map((s) => s.tenant_id ?? "unknown")).size,
    [query.data],
  );

  if (query.isLoading) return <CardSkeleton title="Platform overview" rows={6} />;
  if (query.error) {
    const msg = query.error instanceof ApiError ? query.error.message : "Failed to load";
    return <CardError title="Platform overview" message={msg} />;
  }

  const totalSystems = query.data?.length ?? 0;

  return (
    <div className="space-y-4">
      {summary.loaded && summary.totalHigh > 0 && <ComplianceBanner summary={summary} />}

      <section className="card">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold inline-flex items-center">
              Platform overview
              <InfoTip {...TIPS.alertBadges} />
            </h1>
            <p className="text-sm text-ink-300 mt-1">
              {q
                ? `${filteredSorted.length} of ${totalSystems} system${totalSystems === 1 ? "" : "s"} match`
                : `${totalTenants} tenant${totalTenants === 1 ? "" : "s"} · ${totalSystems} system${totalSystems === 1 ? "" : "s"}`}
            </p>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <SortSelect value={sort} onChange={setSort} showTenant />
            <div className="flex-1 sm:w-80">
              <SearchBox
                value={q}
                onChange={setQ}
                placeholder="Search PWS, tenant, or PWS ID"
              />
            </div>
          </div>
        </header>
      </section>

      {actionQuery.data && (actionQuery.data.items.length > 0 || actionQuery.data.systems.length > 0) && (
        <ActionQueue data={actionQuery.data} asOf={actionQuery.data.as_of} />
      )}

      {flatMode ? (
        <SearchResults
          items={flatVisible}
          empty={filteredSorted.length === 0}
          hasMore={flatHasMore}
          sentinelRef={flatSentinel}
          summary={summary}
          query={q}
          focusedIndex={flatNav.focusedIndex}
          setFocusedIndex={flatNav.setFocusedIndex}
          itemRef={flatNav.itemRef}
        />
      ) : (
        <>
          {groupVisible.map((group) => (
            <section key={group.tenantId} className="card">
              <header className="flex items-baseline justify-between mb-3">
                <h2 className="text-md font-semibold">{group.tenantName}</h2>
                <span className="text-xs text-ink-300">
                  {group.systems.length} system{group.systems.length === 1 ? "" : "s"}
                </span>
              </header>
              <ul className="divide-y divide-ink-700">
                {group.systems.map((s) => (
                  <SystemRow key={s.pws_id} s={s} summary={summary} />
                ))}
              </ul>
            </section>
          ))}
          {groupHasMore && (
            <div ref={groupSentinel} className="py-4 text-center text-xs text-ink-400">
              Loading more tenants…
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SystemRow({
  s,
  summary,
}: {
  s: SystemSummary;
  summary: SummaryView;
}): JSX.Element {
  return (
    <li>
      <Link
        to={`/systems/${encodeURIComponent(s.pws_id)}`}
        className="block py-2 hover:bg-ink-700/30 -mx-2 px-2 rounded transition-colors"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <span className="font-medium truncate block">{s.pws_name}</span>
          </div>
          <SystemBadges
            enriched={summary.byPwsId.get(s.pws_id)}
            loading={summary.loading}
          />
          <span className="font-mono text-xs text-ink-300 shrink-0 w-20 text-right">
            #{s.pws_id}
          </span>
        </div>
      </Link>
    </li>
  );
}

interface SearchResultsProps {
  items: SystemSummary[];
  empty: boolean;
  hasMore: boolean;
  sentinelRef: (node: HTMLElement | null) => void;
  summary: SummaryView;
  query: string;
  focusedIndex: number;
  setFocusedIndex: (i: number) => void;
  itemRef: (i: number) => (node: HTMLElement | null) => void;
}

function SearchResults({
  items,
  empty,
  hasMore,
  sentinelRef,
  summary,
  query,
  focusedIndex,
  setFocusedIndex,
  itemRef,
}: SearchResultsProps): JSX.Element {
  if (empty) {
    return (
      <section className="card">
        <p className="text-sm text-ink-300">No systems match that search.</p>
      </section>
    );
  }
  return (
    <section className="card">
      <ul className="divide-y divide-ink-700">
        {items.map((s, i) => (
          <li key={s.pws_id} ref={itemRef(i)}>
            <Link
              to={`/systems/${encodeURIComponent(s.pws_id)}`}
              onMouseEnter={() => setFocusedIndex(i)}
              className={`block py-2 -mx-2 px-2 rounded transition-colors ${
                focusedIndex === i
                  ? "bg-ink-700/40 ring-1 ring-ink-500"
                  : "hover:bg-ink-700/30"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">
                    <Highlight text={s.pws_name} query={query} />
                  </p>
                  <p className="text-xs text-ink-300 truncate">
                    <Highlight text={s.tenant_name ?? "—"} query={query} />
                  </p>
                </div>
                <SystemBadges
                  enriched={summary.byPwsId.get(s.pws_id)}
                  loading={summary.loading}
                />
                <span className="font-mono text-xs text-ink-300 shrink-0 w-20 text-right">
                  #<Highlight text={s.pws_id} query={query} />
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
      {hasMore && (
        <div ref={sentinelRef} className="py-4 text-center text-xs text-ink-400">
          Loading more…
        </div>
      )}
    </section>
  );
}

function ComplianceBanner({ summary }: { summary: SummaryView }): JSX.Element {
  return (
    <section className="card ring-severity-high/40 bg-severity-high/5">
      <p className="text-sm">
        <span className="pill-high mr-2">{summary.totalHigh} HIGH</span>
        across {summary.systemsWithHigh} system{summary.systemsWithHigh === 1 ? "" : "s"}
        {summary.totalMedium > 0 && (
          <>
            {" "}· <span className="pill-medium">{summary.totalMedium} MEDIUM</span>
          </>
        )}
        {summary.totalLow > 0 && (
          <>
            {" "}· <span className="pill-low">{summary.totalLow} LOW</span>
          </>
        )}
      </p>
    </section>
  );
}
